import React, { useEffect, useState } from 'react';
import { submitToFormspree, PREFERRED_TIME_LABELS } from '../utils/formspree';

function ConsultationModal({ isOpen, onClose, interest = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredTime: '',
    interest: '',
    honeypot: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({ ...prev, interest: interest || prev.interest }));
      setSubmitStatus(null);
    }
  }, [isOpen, interest]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    if (formData.honeypot) {
      setSubmitStatus({ success: true, message: 'Consultation scheduled successfully!' });
      setIsSubmitting(false);
      return;
    }

    try {
      await submitToFormspree({
        formType: 'Consultation Booking',
        subject: `Consultation request from ${formData.name}`,
        fields: {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          preferredTime:
            PREFERRED_TIME_LABELS[formData.preferredTime] || formData.preferredTime,
          interest: formData.interest,
        },
      });

      setSubmitStatus({ success: true, message: 'Consultation scheduled successfully!' });
      setFormData({
        name: '',
        phone: '',
        email: '',
        preferredTime: '',
        interest: '',
        honeypot: '',
      });
    } catch (error) {
      console.error('Error scheduling consultation:', error);
      setSubmitStatus({
        success: false,
        message: error.message || 'Failed to schedule consultation',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-ink-muted hover:text-ink"
          aria-label="Close consultation form"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <p className="section-kicker mb-2">Free consultation</p>
        <h2 className="mb-2 font-display text-3xl font-semibold text-primary-700">
          Book Your Visit
        </h2>
        <p className="mb-6 text-sm text-ink-muted">
          Private appointment. No pressure — just clear guidance on your options.
        </p>

        {formData.interest ? (
          <p className="mb-4 rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-800">
            Interest: <span className="font-semibold">{formData.interest}</span>
          </p>
        ) : null}

        {submitStatus && (
          <div
            className={`mb-4 rounded-lg p-4 ${
              submitStatus.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            {submitStatus.message}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="mb-2 block text-ink-soft" htmlFor="name">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Your name"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-ink-soft" htmlFor="phone">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Your phone number"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-ink-soft" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Your email"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-ink-soft" htmlFor="preferredTime">
              Preferred Time
            </label>
            <select
              id="preferredTime"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              className="w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select a time</option>
              <option value="morning">Morning (9AM - 12PM)</option>
              <option value="afternoon">Afternoon (12PM - 4PM)</option>
              <option value="evening">Evening (4PM - 7PM)</option>
            </select>
          </div>

          <div className="hidden" aria-hidden="true">
            <label htmlFor="honeypot">Leave this empty</label>
            <input
              type="text"
              id="honeypot"
              name="honeypot"
              value={formData.honeypot || ''}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            className="w-full rounded-full bg-primary-600 py-3 font-semibold text-white transition duration-300 hover:bg-primary-700 disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Scheduling...' : 'Schedule Consultation'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ConsultationModal;
